// UI copy for both languages. Keep keys identical between `en` and `id`.
// Names, schools and tech names stay untranslated.

export const translations = {
  en: {
    languageToggle: 'Language',
    backToHome: 'Back to Homepage',

    home: {
      roles: ['Creative Designer', 'Frontend Developer', 'UI/UX Designer'],
      aboutMe: 'About me',
      contactMe: 'Contact me',
      goTo: 'Go to',
    },

    cards: {
      about: { label: 'About', subtitle: 'Frontend Developer & UI/UX Designer' },
      projects: { label: 'Projects', subtitle: (n) => `${n} featured builds` },
      techStack: { label: 'Tech Stack', subtitle: (n) => `${n} tools & languages` },
      certificates: { label: 'Certificates', subtitle: (n) => `${n} certifications earned` },
      contact: { label: 'Contact', subtitle: "Let's build something together" },
    },

    projects: {
      eyebrow: 'Selected Works',
      title: 'Projects',
      builds: (n) => `${n} builds`,
      play: 'Play now',
      visit: 'Visit site',
      code: 'Code',
      viewCode: 'View code',
      kinds: { game: 'Game', web: 'Web', code: 'Open source' },
      demo: 'Demo',
      hintDesktop: 'Scroll, drag, or use the arrow keys to browse',
      hintMobile: 'Swipe left or right to browse',
      hintEsc: 'Press ESC to go back',
    },

    techStack: {
      eyebrow: 'Toolkit',
      title: 'Tech Stack',
      count: (n) => `${n} tools`,
      all: 'All',
      hint: 'Tap a tool to see where I used it',
      usedIn: 'Used in',
      notYet: 'Part of my everyday workflow, no public repo to show yet.',
      close: 'Close',
      thisPortfolio: 'This portfolio',
    },

    certificates: {
      eyebrow: 'Learning log',
      title: 'Certificates',
      summary: (n) => `${n} courses completed on MySkill`,
      all: 'All',
      topics: { ai: 'Artificial Intelligence', web: 'Web Fundamentals' },
      hint: 'Tap a certificate to view it full size',
      open: 'Open original',
      prev: 'Previous certificate',
      next: 'Next certificate',
      close: 'Close',
      of: 'of',
      locale: 'en-US',
    },

    contact: {
      eyebrow: 'Contact',
      title: "Let's talk.",
      lead: 'Have a project, a role, or just an idea? Pick a channel, or write a quick note and send it through email or WhatsApp.',
      available: 'Open to work',
      localTime: 'Local time in Indonesia',
      channels: 'Channels',
      copy: 'Copy',
      copied: 'Copied',
      composer: 'Quick message',
      name: 'Your name',
      namePlaceholder: 'Who am I talking to?',
      topic: 'Topic',
      topics: { freelance: 'Freelance project', fulltime: 'Full-time role', collab: 'Collaboration', other: 'Just saying hi' },
      message: 'Message',
      messagePlaceholder: 'Tell me a little about what you have in mind',
      sendEmail: 'Send via email',
      sendWhatsApp: 'Send via WhatsApp',
      composerNote: 'Opens your email app or WhatsApp with the message filled in. Nothing is stored on this site.',
      greeting: (name) => (name ? `Hi Vesa, I'm ${name}.` : 'Hi Vesa,'),
      downloadCv: 'Download CV (PDF)',
      email: 'Your email',
      emailPlaceholder: 'So I can reply to you',
      send: 'Send message',
      sending: 'Sending…',
      sentTitle: 'Message sent.',
      sentBody: "Thanks! It's in my inbox now, and I'll reply to your email soon.",
      sendAnother: 'Send another message',
      sendError: "Couldn't send right now. Please try again, or use WhatsApp.",
      orWhatsApp: 'Prefer chat?',
      directNote: 'Your message goes straight to my inbox. Your email is only used to reply.',
    },

    about: {
      eyebrow: 'About me',
      role: 'Frontend Developer & UI/UX Designer',
      scrollHint: 'Scroll',
      facts: {
        based: 'Based in',
        basedValue: 'Indonesia',
        focus: 'Focus',
        focusValue: 'Frontend & UI/UX',
        status: 'Status',
        statusValue: 'Open to work',
      },
      statementLead: 'Frontend Developer & UI/UX Designer',
      statementRest:
        ', turning creative concepts and user needs into modern, accessible web interfaces that feel obvious.',
      bio: [
        "I'm {fullName} (call me {nickname}). A Frontend Developer and UI/UX Designer with over 2 years of experience creating modern, responsive, user-centered web applications.",
        'My journey started with a fascination for how a good interface can change the way people feel about a product. Today I turn design mockups into pixel-perfect, interactive websites with React, Tailwind CSS and modern frontend tooling.',
        'For me, great design is more than looks. It is smooth functionality, accessibility, and digital journeys that real people can rely on every day.',
      ],
      stats: [
        { value: '30+', label: 'Projects completed' },
        { value: '2+', label: 'Years of experience' },
        { value: '15+', label: 'Happy clients' },
      ],
      servicesTitle: 'What I do',
      services: [
        {
          title: 'Frontend Development',
          desc: 'Responsive, fast interfaces in React and Tailwind CSS, from landing pages to full web apps.',
        },
        {
          title: 'UI/UX Design',
          desc: 'Wireframes, prototypes and design systems in Figma, shaped around how people actually use a product.',
        },
        {
          title: 'Creative Design',
          desc: 'Visual identity, motion and small details that give a product its own character.',
        },
      ],
      toolkitTitle: 'Toolkit',
      categories: {
        Frontend: 'Frontend',
        Language: 'Language',
        Backend: 'Backend',
        Database: 'Database',
        Game: 'Game Dev',
        Design: 'Design',
        Tools: 'Tools',
      },
      educationTitle: 'Education',
      education: [
        { title: 'Digital Technology University of Indonesia', desc: 'University' },
      ],
      outroTitle: "Let's build something together.",
      outroBody:
        "When I'm not coding or designing, I'm exploring web trends, experimenting with design systems, or tinkering with open-source ideas.",
      availability: 'Available for freelance projects and full-time roles. Based in Indonesia, working worldwide.',
      downloadCv: 'Download CV (PDF)',
      sayHello: 'Say hello',
    },
  },

  id: {
    languageToggle: 'Bahasa',
    backToHome: 'Kembali ke Beranda',

    home: {
      roles: ['Creative Designer', 'Frontend Developer', 'UI/UX Designer'],
      aboutMe: 'Tentang saya',
      contactMe: 'Hubungi saya',
      goTo: 'Buka',
    },

    cards: {
      about: { label: 'Tentang', subtitle: 'Frontend Developer & UI/UX Designer' },
      projects: { label: 'Proyek', subtitle: (n) => `${n} proyek pilihan` },
      techStack: { label: 'Tech Stack', subtitle: (n) => `${n} tools & bahasa` },
      certificates: { label: 'Sertifikat', subtitle: (n) => `${n} sertifikat diraih` },
      contact: { label: 'Kontak', subtitle: 'Yuk, bangun sesuatu bersama' },
    },

    projects: {
      eyebrow: 'Karya Pilihan',
      title: 'Proyek',
      builds: (n) => `${n} proyek`,
      play: 'Main sekarang',
      visit: 'Buka situs',
      code: 'Kode',
      viewCode: 'Lihat kode',
      kinds: { game: 'Game', web: 'Web', code: 'Open source' },
      demo: 'Demo',
      hintDesktop: 'Scroll, geser, atau pakai tombol panah untuk menjelajah',
      hintMobile: 'Geser ke kiri atau kanan untuk berpindah',
      hintEsc: 'Tekan ESC untuk kembali',
    },

    techStack: {
      eyebrow: 'Toolkit',
      title: 'Tech Stack',
      count: (n) => `${n} tools`,
      all: 'Semua',
      hint: 'Ketuk salah satu untuk melihat di mana saya memakainya',
      usedIn: 'Dipakai di',
      notYet: 'Bagian dari alur kerja harian, belum ada repo publik untuk ditunjukkan.',
      close: 'Tutup',
      thisPortfolio: 'Portofolio ini',
    },

    certificates: {
      eyebrow: 'Catatan belajar',
      title: 'Sertifikat',
      summary: (n) => `${n} kursus selesai di MySkill`,
      all: 'Semua',
      topics: { ai: 'Artificial Intelligence', web: 'Dasar Web' },
      hint: 'Ketuk sertifikat untuk melihat ukuran penuh',
      open: 'Buka asli',
      prev: 'Sertifikat sebelumnya',
      next: 'Sertifikat berikutnya',
      close: 'Tutup',
      of: 'dari',
      locale: 'id-ID',
    },

    contact: {
      eyebrow: 'Kontak',
      title: 'Yuk, ngobrol.',
      lead: 'Punya proyek, lowongan, atau sekadar ide? Pilih salah satu kanal, atau tulis pesan singkat lalu kirim lewat email atau WhatsApp.',
      available: 'Terbuka untuk kerja',
      localTime: 'Waktu lokal di Indonesia',
      channels: 'Kanal',
      copy: 'Salin',
      copied: 'Tersalin',
      composer: 'Pesan cepat',
      name: 'Nama kamu',
      namePlaceholder: 'Dengan siapa saya bicara?',
      topic: 'Topik',
      topics: { freelance: 'Proyek freelance', fulltime: 'Posisi full-time', collab: 'Kolaborasi', other: 'Sekadar menyapa' },
      message: 'Pesan',
      messagePlaceholder: 'Ceritakan sedikit tentang yang kamu bayangkan',
      sendEmail: 'Kirim lewat email',
      sendWhatsApp: 'Kirim lewat WhatsApp',
      composerNote: 'Membuka aplikasi email atau WhatsApp dengan pesan yang sudah terisi. Tidak ada data yang disimpan di situs ini.',
      greeting: (name) => (name ? `Halo Vesa, saya ${name}.` : 'Halo Vesa,'),
      downloadCv: 'Unduh CV (PDF)',
      email: 'Email kamu',
      emailPlaceholder: 'Supaya saya bisa membalas',
      send: 'Kirim pesan',
      sending: 'Mengirim…',
      sentTitle: 'Pesan terkirim.',
      sentBody: 'Terima kasih! Pesanmu sudah masuk ke inbox saya, dan akan segera saya balas ke email kamu.',
      sendAnother: 'Kirim pesan lain',
      sendError: 'Pesan belum bisa terkirim. Coba lagi, atau pakai WhatsApp.',
      orWhatsApp: 'Lebih suka chat?',
      directNote: 'Pesanmu langsung masuk ke inbox saya. Email kamu hanya dipakai untuk membalas.',
    },

    about: {
      eyebrow: 'Tentang saya',
      role: 'Frontend Developer & UI/UX Designer',
      scrollHint: 'Gulir',
      facts: {
        based: 'Domisili',
        basedValue: 'Indonesia',
        focus: 'Fokus',
        focusValue: 'Frontend & UI/UX',
        status: 'Status',
        statusValue: 'Terbuka untuk kerja',
      },
      statementLead: 'Frontend Developer & UI/UX Designer',
      statementRest:
        ' yang mengubah ide kreatif dan kebutuhan pengguna menjadi antarmuka web modern, mudah diakses, dan terasa natural.',
      bio: [
        'Saya {fullName} (panggil saja {nickname}). Frontend Developer dan UI/UX Designer dengan pengalaman lebih dari 2 tahun membangun aplikasi web yang modern, responsif, dan berpusat pada pengguna.',
        'Perjalanan saya berawal dari rasa penasaran tentang bagaimana antarmuka yang baik bisa mengubah cara orang merasakan sebuah produk. Sekarang saya mengubah desain mockup menjadi website interaktif yang presisi dengan React, Tailwind CSS, dan tooling frontend modern.',
        'Bagi saya, desain yang hebat bukan sekadar tampilan. Desain yang hebat berarti fungsi yang mulus, aksesibel, dan pengalaman digital yang bisa diandalkan setiap hari.',
      ],
      stats: [
        { value: '30+', label: 'Proyek selesai' },
        { value: '2+', label: 'Tahun pengalaman' },
        { value: '15+', label: 'Klien puas' },
      ],
      servicesTitle: 'Yang saya kerjakan',
      services: [
        {
          title: 'Frontend Development',
          desc: 'Antarmuka yang responsif dan cepat dengan React dan Tailwind CSS, dari landing page sampai aplikasi web utuh.',
        },
        {
          title: 'UI/UX Design',
          desc: 'Wireframe, prototipe, dan design system di Figma yang dirancang sesuai cara orang benar-benar memakai produk.',
        },
        {
          title: 'Creative Design',
          desc: 'Identitas visual, animasi, dan detail kecil yang memberi produk karakternya sendiri.',
        },
      ],
      toolkitTitle: 'Toolkit',
      categories: {
        Frontend: 'Frontend',
        Language: 'Bahasa',
        Backend: 'Backend',
        Database: 'Database',
        Game: 'Game Dev',
        Design: 'Desain',
        Tools: 'Tools',
      },
      educationTitle: 'Pendidikan',
      education: [
        { title: 'Universitas Teknologi Digital Indonesia', desc: 'Universitas' },
      ],
      outroTitle: 'Yuk, bangun sesuatu bersama.',
      outroBody:
        'Di luar ngoding dan mendesain, saya suka mengikuti tren web, bereksperimen dengan design system, atau mengulik ide-ide open source.',
      availability: 'Terbuka untuk proyek freelance dan posisi full-time. Berdomisili di Indonesia, siap bekerja dari mana saja.',
      downloadCv: 'Unduh CV (PDF)',
      sayHello: 'Sapa saya',
    },
  },
};
